import { Component, computed, forwardRef, inject, input, OnInit, signal, viewChild } from '@angular/core';
import {
  ControlValueAccessor,
  FormControlDirective,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { IFormInput } from '@mwe/models';
import { FormComponent } from '../../form.component';
import { TranslatePipe } from '@ngx-translate/core';
import { ProductIconComponent } from '../../../product-icon/product-icon.component';
import { ClickPreventDefaultDirective } from '../../../directives/click/click-prevent-default.directive';
import { NgClass } from '@angular/common';
import { CorrelatedCompanyReferencePipe } from '@mwe/ui/pipes';

@Component({
  selector: 'mwe-selectable-card-checkbox-input',
  templateUrl: './selectable-card-checkbox-input.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: SelectableCardCheckboxInputComponent,
      multi: true,
    },
  ],
  imports: [
    ProductIconComponent,
    FormsModule,
    ClickPreventDefaultDirective,
    ReactiveFormsModule,
    NgClass,
    CorrelatedCompanyReferencePipe,
    TranslatePipe,
  ],
})
export class SelectableCardCheckboxInputComponent implements ControlValueAccessor, OnInit {
  private readonly formComponent = inject(
    forwardRef(() => FormComponent),
    { optional: true },
  );

  readonly parentForm = input<UntypedFormGroup>();
  readonly inputElem = input<IFormInput>();
  readonly isLoading = input<boolean>(false);

  readonly formControlDirective = viewChild<FormControlDirective>(FormControlDirective);

  readonly isRequired = signal<boolean>(undefined);

  readonly isValid = computed<boolean>(() => this.formControlDirective()?.control?.valid);
  readonly control = computed<UntypedFormControl>(() => this.parentForm().controls[this.inputElem().name] as UntypedFormControl);
  readonly ariaDescribedBy = computed<string>(() => {
    if (this.formComponent) {
      // Error State hat Vorrang für Screenreader
      if (this.formComponent.isInputErrorVisible(this.inputElem().componentType)) {
        return `form-input-desc--${this.inputElem().name}`;
      } else if (this.formComponent.isInputHelpInfoVisible(this.inputElem())) {
        return `form-input-info--${this.inputElem().name}`;
      }
    }
    return undefined;
  });

  ngOnInit(): void {
    this.isRequired.set(this.inputElem().validators?.indexOf(Validators.required) > -1);
  }

  registerOnTouched(fn: any): void {
    this.formControlDirective().valueAccessor.registerOnTouched(fn);
  }

  registerOnChange(fn: any): void {
    this.formControlDirective().valueAccessor.registerOnChange(fn);
  }

  writeValue(obj: any): void {
    this.formControlDirective().valueAccessor.writeValue(obj);
  }

  setDisabledState(isDisabled: boolean): void {
    this.formControlDirective().valueAccessor.setDisabledState(isDisabled);
  }
}
